import debounce from 'lodash/debounce'
import type { NextRouter } from 'next/router'
import qs from 'qs'
import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react'
import { useInstantSearch } from 'react-instantsearch'

import useAiSearchStore from '@/stores/aiSearchStore'

export const useSyncAISearchToRouter = (
	router: NextRouter,
	user: User
): void => {
	const { searchByAudio, searchByPrompt, clearAiSearch } = useAiSearchStore()
	const { setIndexUiState } = useInstantSearch()
	const isInitialMount = useRef(true)
	const isBrowserNavigation = useRef(false)

	// reset the search and scroll to the top of the page when the user clicks on a search link
	useEffect(() => {
		const resetSearch = (event: MouseEvent): void => {
			const eventTarget = event.target
			if (!(eventTarget instanceof Element)) {
				return
			}

			const searchLink = eventTarget.closest('a[href^="/track/searchkeyword"]')
			if (!searchLink) {
				return
			}

			sessionStorage.removeItem('activeSearchScrollPosition')
			sessionStorage.removeItem('activeSearchTrackRow')
			clearAiSearch()
			setIndexUiState((prevState) => ({
				...prevState,
				menu: null,
				query: '',
				range: null,
				refinementList: null,
				sortBy: null,
				toggle: null,
			}))

			document.getElementById('main-content')?.scrollTo(0, 0)
		}

		// Capture + document delegation so remounted nav links still reset search.
		document.addEventListener('click', resetSearch, true)

		return () => {
			document.removeEventListener('click', resetSearch, true)
		}
	}, [clearAiSearch, setIndexUiState])

	useEffect(() => {
		const handlePopState = (): void => {
			isBrowserNavigation.current = true
		}

		window.addEventListener('popstate', handlePopState)
		return () => window.removeEventListener('popstate', handlePopState)
	}, [])

	const debouncedPush = useMemo(
		() =>
			debounce((query: Record<string, string | string[]>) => {
				router.push(
					{ pathname: router.pathname, query: qs.stringify(query) },
					undefined,
					{
						shallow: true,
					}
				)
			}, 300),
		[router]
	)

	useEffect(() => {
		const { query } = router
		let hasChanges = false

		if (isInitialMount.current || isBrowserNavigation.current) {
			isInitialMount.current = false
			isBrowserNavigation.current = false
			return
		}

		const mergedQuery = { ...query } as Record<string, string | string[]>

		// Only build query for the two params we care about
		if (user && searchByAudio?.url) {
			if (query.searchByAudio !== searchByAudio.url) {
				mergedQuery.searchByAudio = searchByAudio.url
				hasChanges = true
			}
		} else if (query.searchByAudio) {
			delete mergedQuery.searchByAudio
			hasChanges = true
		}

		if (user && searchByPrompt) {
			if (query.searchByPrompt !== searchByPrompt) {
				mergedQuery.searchByPrompt = searchByPrompt
				hasChanges = true
			}
		} else if (query.searchByPrompt) {
			delete mergedQuery.searchByPrompt
			hasChanges = true
		}

		// Only push if something actually changed
		if (hasChanges) {
			debouncedPush(mergedQuery)
		}

		return () => {
			debouncedPush.cancel()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchByAudio, searchByPrompt, user])
}

type UsePersistSearchScrollPositionProps = {
	clearSearchScrollPosition: () => void
	containerRef: MutableRefObject<HTMLDivElement | null>
}

export const usePersistSearchScrollPosition =
	(): UsePersistSearchScrollPositionProps => {
		const [hasScrolledOnMount, setHasScrolledOnMount] = useState(false)

		const containerRef = useRef<HTMLDivElement>(null)

		const captureClick = (event: MouseEvent): void => {
			const target = event.target as HTMLElement

			/**
			 * We watch for any click in the results that navigates away from the page.
			 */
			if (target.closest('a')) {
				const mainContent = document.getElementById('main-content')
				const trackRow = target.closest('[id^="track-row-"]')

				if (mainContent && trackRow) {
					const scrollPosition = mainContent.scrollTop
					sessionStorage.setItem(
						'activeSearchScrollPosition',
						scrollPosition.toString()
					)
					sessionStorage.setItem('activeSearchTrackRow', trackRow.id)
				}
			}
		}

		const scrollToTrackRow = (container: HTMLElement): void => {
			const scrollPosition = sessionStorage.getItem(
				'activeSearchScrollPosition'
			)
			const trackRowId = sessionStorage.getItem('activeSearchTrackRow')
			const mainContent = document.getElementById('main-content')

			if (scrollPosition && trackRowId && mainContent) {
				/**
				 * We wait for the track row to be in the DOM before scrolling to it.
				 */
				const observer = new MutationObserver((_, obs) => {
					const targetElement = document.getElementById(trackRowId)
					if (targetElement) {
						mainContent.scrollTo(0, parseInt(scrollPosition))
						obs.disconnect()
					}
				})

				observer.observe(container, {
					childList: true,
					subtree: true,
				})
			}
		}

		const clearSearchScrollPosition = (): void => {
			sessionStorage.removeItem('activeSearchScrollPosition')
			sessionStorage.removeItem('activeSearchTrackRow')
		}

		useEffect(() => {
			if (!hasScrolledOnMount && containerRef.current) {
				scrollToTrackRow(containerRef.current)
				setHasScrolledOnMount(true)
			}
		}, [hasScrolledOnMount])

		useEffect(() => {
			const container = containerRef.current

			if (container) {
				container.addEventListener('click', captureClick)
			}

			return () => {
				if (container) {
					container.removeEventListener('click', captureClick)
				}
			}
		}, [hasScrolledOnMount])

		return {
			clearSearchScrollPosition,
			containerRef,
		}
	}
